import React from 'react';

import { useHistory } from '../../../hooks';
import { useListFetch } from '../../../hooks';
import { useCampaignStates } from '../../hooks';
import { DateFilter } from '../../../components';
import { useViewContactState } from '../../hooks';
import { useAppContext } from '../../../molecules';
import { Slug, Button } from '../../../components';
import { PaginatePanel } from '../../../components';
import ContactStateViewModal from '../ContactStateViewModal';
import { Form, FormItem, TopPanel } from '../../../components';
import { SearchBar, Dropdown, Table } from '../../../components';
import type { ColumnType } from '../../../components';
import type { CampaignMonitoringItemType } from '../../../api';
import type { GetCampaignMonitoringFilters } from '../../../api';

type ListResultType<
  T extends CampaignMonitoringItemType = CampaignMonitoringItemType,
> = ReturnType<typeof useListFetch<T, GetCampaignMonitoringFilters>>;

type MonitoringTemplateProps<
  T extends CampaignMonitoringItemType = CampaignMonitoringItemType,
> = {
  totalCount?: number;
  campaignId?: string;
  columns: ColumnType<T>[];
  listResult: ListResultType<T>;
  stateOptions?: OptionType<LatestContactState>[];
};

const MonitoringTemplate = <
  T extends CampaignMonitoringItemType = CampaignMonitoringItemType,
>({
  columns,
  totalCount,
  campaignId,
  listResult,
  stateOptions: initialStateOptions,
}: MonitoringTemplateProps<T>) => {
  const history = useHistory();
  const { meta } = useAppContext();
  const { stateOptions } = useCampaignStates(campaignId, initialStateOptions);
  const {
    state,
    onClose,
    isOpened,
    contactId,
    loading: loadingView,
    handleView,
  } = useViewContactState();
  const {
    nodes,
    filters,
    setFilters,
    reloading,
    refresh,
    sort,
    setSort,
    pagination,
    setPage,
    initializing: loading,
  } = listResult;

  return (
    <div className="flex flex-col gap-y-md w-full">
      <ContactStateViewModal
        state={state}
        isOpened={isOpened}
        onClose={onClose}
      />
      <Form<NonNullable<typeof filters>>
        initialValue={filters}
        onChange={setFilters}
      >
        <TopPanel
          topLeftContent={
            <FormItem<NonNullable<typeof filters>> name="search" label="">
              <SearchBar />
            </FormItem>
          }
          buttons={[
            {
              icon: 'refresh',
              label: 'Refresh',
              onClick: refresh,
              loading: reloading,
            },
          ]}
        >
          <div className="mr-auto flex items-center gap-x-md min-w-[20rem]">
            Total Records:
            <Slug
              className="min-w-[3rem] max-w-[7rem] w-fit"
              text={totalCount || pagination.totalItems}
            />
          </div>

          <FormItem<NonNullable<typeof filters>>
            label=""
            name="latestContactStateEnum"
          >
            <Dropdown<LatestContactState>
              grow={false}
              placeholder="All Statuses"
              options={stateOptions}
            />
          </FormItem>
          <FormItem<NonNullable<typeof filters>> name="campaignId" label="">
            <Dropdown
              grow={false}
              placeholder="Campaign"
              options={[
                { value: '', label: 'All Campaigns' },
                ...meta.campaigns,
              ]}
              value={campaignId || ''}
              onChange={id =>
                history.push(
                  id ? `/campaign_monitoring/${id}` : '/campaign_monitoring'
                )
              }
            />
          </FormItem>
          <DateFilter<NonNullable<typeof filters>>
            toName="updatedTo"
            fromName="updatedFrom"
          />
        </TopPanel>
      </Form>
      <PaginatePanel {...pagination} setPage={setPage} />
      <div className="flex-1 overflow-y-auto">
        <Table
          data={nodes}
          loading={loading}
          sort={sort}
          onSort={setSort}
          config={{
            columns: [
              ...columns,
              {
                header: 'Actions',
                align: 'right',
                cellClassName: 'min-w-[20rem]',
                getValue: item => (
                  <Button
                    icon="question"
                    width="small"
                    tooltip="Contact State Details"
                    onClick={() => handleView(item.contact_id)}
                    loading={loadingView && contactId === item.contact_id}
                  />
                ),
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default React.memo(MonitoringTemplate) as typeof MonitoringTemplate;
